/**
 * Settings → History: the signed-in person's own actions, newest first.
 * Pangaea returns only that principal's events and withholds a record they
 * can no longer read. This section groups the page by day and links each
 * readable row to the record.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { SpecraActivityRow, SpecraButton, SpecraEmptyState, SpecraFilterToolbar, SpecraFormField, SpecraHeading, SpecraInlineBanner, SpecraInput, SpecraLink, SpecraSelect, SpecraSpinner, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useInfiniteQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { projectDataClient } from '../../../lib/connect/clients';
import { humanizeDiffs } from '../../Delivery/_shared/components/opsAuditFormat';
import { ACTIVITY_KIND_OPTIONS, activityHref, utcDayKey, utcDayLabel } from '../activityHistory';
const PAGE_SIZE = 25;
function toView(entry) {
    const occurredAt = entry.occurredAt?.toDate();
    if (!occurredAt || !entry.id) return null;
    return {
        id: entry.id,
        summary: entry.summary || 'You recorded an action',
        occurredAt,
        href: activityHref({
            recordHidden: entry.recordHidden,
            objectType: entry.objectType,
            objectId: entry.objectId,
            programId: entry.programId
        }),
        detailsWithheld: entry.detailsWithheld,
        fieldDiffs: entry.fieldDiffs ?? [],
        previousStateJson: entry.previousStateJson ?? '',
        newStateJson: entry.newStateJson ?? ''
    };
}
function useMyActivity(kind, since, until) {
    return useInfiniteQuery({
        queryKey: [
            'settings-history',
            kind,
            since,
            until
        ],
        initialPageParam: '',
        queryFn: ({ pageParam })=>projectDataClient.listMyActivity({
                cursor: pageParam,
                limit: PAGE_SIZE,
                kind,
                since: since ? Timestamp.fromDate(new Date(`${since}T00:00:00.000Z`)) : undefined,
                until: until ? Timestamp.fromDate(new Date(`${until}T23:59:59.999Z`)) : undefined
            }),
        getNextPageParam: (last)=>last.nextCursor || undefined
    });
}
function renderActivityLink({ href, ref, ...props }) {
    return /*#__PURE__*/ _jsx(Link, {
        ref: ref,
        to: href,
        ...props
    });
}
export function HistorySettings() {
    const [kind, setKind] = useState('');
    const [since, setSince] = useState('');
    const [until, setUntil] = useState('');
    const [openId, setOpenId] = useState(null);
    const query = useMyActivity(kind, since, until);
    const entries = (query.data?.pages ?? []).flatMap((page)=>(page.entries ?? []).flatMap((entry)=>{
            const view = toView(entry);
            return view ? [
                view
            ] : [];
        }));
    const filtered = kind !== '' || since !== '' || until !== '';
    const groups = groupByDay(entries);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        "data-testid": Testids.settingsHistory.section,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx(SpecraHeading, {
                        size: "sm",
                        children: "History"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        tone: "muted",
                        children: "What you did in Specra, newest first. A record you can no longer open still shows that you acted, without its name or its values."
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraFilterToolbar, {
                search: false,
                filters: /*#__PURE__*/ _jsxs("div", {
                    className: "flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-end",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            className: "w-full sm:w-48",
                            label: "Kind",
                            children: /*#__PURE__*/ _jsx(SpecraSelect, {
                                "aria-label": "Activity kind",
                                "data-testid": Testids.settingsHistory.kindFilter,
                                onChange: (event)=>setKind(event.target.value),
                                size: "sm",
                                value: kind,
                                children: ACTIVITY_KIND_OPTIONS.map((option)=>/*#__PURE__*/ _jsx("option", {
                                        value: option.value,
                                        children: option.label
                                    }, option.value || 'all'))
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            className: "w-full sm:w-44",
                            label: "From",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                "aria-label": "From",
                                "data-testid": Testids.settingsHistory.since,
                                onChange: (event)=>setSince(event.target.value),
                                type: "date",
                                value: since
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            className: "w-full sm:w-44",
                            label: "To",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                "aria-label": "To",
                                "data-testid": Testids.settingsHistory.until,
                                onChange: (event)=>setUntil(event.target.value),
                                type: "date",
                                value: until
                            })
                        })
                    ]
                })
            }),
            query.isLoading && /*#__PURE__*/ _jsx(SpecraSpinner, {}),
            query.error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                title: "Could not load your history",
                tone: "danger",
                children: query.error instanceof Error ? query.error.message : 'Something went wrong'
            }),
            !query.isLoading && !query.error && entries.length === 0 && /*#__PURE__*/ _jsx(SpecraEmptyState, {
                "data-testid": Testids.settingsHistory.empty,
                description: filtered ? 'Try a different kind or a wider date range.' : 'Approvals, edits and uploads you make will show up here.',
                framed: true,
                icon: /*#__PURE__*/ _jsx(History, {}),
                title: filtered ? 'Nothing matches these filters' : 'No activity yet'
            }),
            groups.length > 0 && /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.settingsHistory.list,
                children: groups.map((group)=>/*#__PURE__*/ _jsxs("section", {
                        "data-day": group.day,
                        "data-testid": Testids.settingsHistory.dayGroup,
                        children: [
                            /*#__PURE__*/ _jsx(SpecraHeading, {
                                className: "mt-4",
                                size: "sm",
                                children: group.label
                            }),
                            group.entries.map((entry)=>/*#__PURE__*/ _jsx(ActivityRow, {
                                    entry: entry,
                                    open: openId === entry.id,
                                    onToggle: ()=>setOpenId(openId === entry.id ? null : entry.id)
                                }, entry.id))
                        ]
                    }, group.day))
            }),
            query.hasNextPage && /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.settingsHistory.loadMore,
                disabled: query.isFetchingNextPage,
                onClick: ()=>void query.fetchNextPage(),
                type: "button",
                variant: "outline",
                children: query.isFetchingNextPage ? 'Loading…' : 'Load more'
            })
        ]
    });
}
function groupByDay(entries) {
    const groups = [];
    for (const entry of entries){
        const day = utcDayKey(entry.occurredAt);
        const last = groups[groups.length - 1];
        if (!last || last.day !== day) {
            groups.push({
                day,
                label: utcDayLabel(day),
                entries: [
                    entry
                ]
            });
        } else {
            last.entries.push(entry);
        }
    }
    return groups;
}
function ActivityRow({ entry, open, onToggle }) {
    const diffs = humanizeDiffs(entry.fieldDiffs);
    const hasPayload = entry.previousStateJson !== '' || entry.newStateJson !== '';
    const canExpand = diffs.length > 0 || hasPayload;
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.settingsHistory.row,
        children: /*#__PURE__*/ _jsx(SpecraActivityRow, {
            actions: canExpand ? /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: onToggle,
                size: "sm",
                type: "button",
                variant: "ghost",
                children: open ? 'Hide changes' : 'Show changes'
            }) : undefined,
            description: entry.detailsWithheld ? 'Changes withheld from your view' : open ? /*#__PURE__*/ _jsx(Detail, {
                diffs: diffs,
                entry: entry
            }) : undefined,
            timestamp: entry.occurredAt.toLocaleString('en-US', {
                timeZone: 'UTC',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                timeZoneName: 'short'
            }),
            title: entry.href ? /*#__PURE__*/ _jsx(SpecraLink, {
                "data-testid": Testids.settingsHistory.rowLink,
                href: entry.href,
                renderLink: renderActivityLink,
                children: entry.summary
            }) : entry.summary
        })
    });
}
function Detail({ diffs, entry }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        "data-testid": Testids.settingsHistory.detail,
        children: [
            diffs.map((diff)=>/*#__PURE__*/ _jsxs(SpecraText, {
                    size: "caption",
                    children: [
                        diff.label,
                        ": ",
                        diff.collapsed ? diff.after : `${diff.before} → ${diff.after}`
                    ]
                }, diff.label)),
            entry.previousStateJson && /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "caption",
                tone: "muted",
                children: [
                    "Before: ",
                    entry.previousStateJson
                ]
            }),
            entry.newStateJson && /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "caption",
                children: [
                    "After: ",
                    entry.newStateJson
                ]
            })
        ]
    });
}
